import Avatar from "@mui/material/Avatar";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { Box, Button, Grid, GridColumn, GridRow, Link, OverflowText, Text } from "designSystem/index";
import { observer } from "mobx-react-lite";
import React, { ReactNode } from "react";
import FroalaEditorView from "react-froala-wysiwyg/FroalaEditorView";
import { ContentContainer, useAttachmentCardStyles, useMessageStyles } from "src/Chat/component/styles";
import UnsupportedBlock from "src/Chat/component/UnsupportedBlock";
import { Attachment, ChatMessage, ChatMessageAttachmentContentType, ChatMessageType, fixDisplayName } from "../api";
import { filterRichText, getLatestReplyFromMessage, getLocaleTimeStringFromMessage, getReactionsFromMessage, useMSTeamsStore, userImageMap } from "./MSTeamsStore";
import { useTheme } from "@mui/material";

interface AttachmentSectionProps {
	attachment: Attachment;

	/** the url to this particular channel */
	channelWebUrl?: string;
}

const AttachmentSection = (props: AttachmentSectionProps) => {
	const classes = useAttachmentCardStyles(useTheme());
	const { attachment, channelWebUrl } = props;
	const {
		content,
		contentType,
		contentUrl,
		name,
	} = attachment;

	let Card: ReactNode;

	switch (contentType) {
		case ChatMessageAttachmentContentType.THUMBNAIL:
			const jsonObj = JSON.parse(content);

			Card = (
				<Grid
					container
					direction="row"
					sx={classes.attachment}
				>
					<Grid
						item
						sx={classes.attachmentBar}
					/>
					<Grid
						item
						sx={classes.attachmentContent}
					>
						<FroalaEditorView model={jsonObj["title"]} />
						<FroalaEditorView model={jsonObj["text"]} />
					</Grid>
				</Grid>
			);
			break;
		case ChatMessageAttachmentContentType.CODE_SNIPPET:
			Card = (
				<Box 
					maxHeight={150} 
					overflow="auto"
					sx={classes.attachmentsContainer}
				>
					<GridRow
						growChild={1}
						sx={classes.attachmentContainer}
					>
						<Text
							sx={classes.codeSnippetText}
							text={content}
						/>
					</GridRow>
				</Box>
			);
			break;
		case ChatMessageAttachmentContentType.REFERENCE:
			Card = (
				<Box sx={classes.attachmentsContainer}>
					<GridRow
						growChild={1}
						sx={classes.attachmentContainer}
					>
						<AttachFileIcon />
						<Link
							href={contentUrl}
							sx={classes.attachment}
							target="_blank"
						>
							<OverflowText text={name} />
						</Link>
					</GridRow>
				</Box>
			);
			break;
		default:
			Card = (
				<UnsupportedBlock link={channelWebUrl} linkTextProps={{ res: "MSTeamsUnsupportedMessageLink" }} />
			);
			break;
	}

	return (
		<>
			{Card}
		</>
	);
};

interface MessageProps {
	/** indicate if this message is a reply to another message */
	isReply?: boolean;

	/** message to render */
	message: ChatMessage;
}

const Message: React.FunctionComponent<MessageProps> = observer((({ message, isReply }) => {
	const classes = useMessageStyles(useTheme());
	const { attachments, body, from, importance, messageType, subject, reactions } = message;
	const timestamp = getLocaleTimeStringFromMessage(message);
	const { selectMessageReplies, selectedMessage, selectedChannelWebUrl, users } = useMSTeamsStore();

	let userId;
	let userName;

	if (from) {
		({ displayName: userName, id: userId } = (from.user || from.application));
		userName = fixDisplayName(userId, userName, users);
	}

	const reactionJSX = [];
	if (reactions.length) {
		const reactionMap = getReactionsFromMessage(reactions);
		for (const reaction in reactionMap) {
			reactionJSX.push(<span key={reaction} style={classes.reaction}>{reaction} {reactionMap[reaction]}</span>);
		}
	}

	// need to replace emoji img elements with unicode emoji strings
	const richText = filterRichText(body.content);

	return (
		<GridRow
			growChild={1}
			sx={{
				...classes.messageContainer,
				marginLeft: isReply ? 8 : "",
				width: isReply ? "initial" : "100%",
			}}
		>
			<Avatar
				alt={userName}
				src={userImageMap[userId]}
				sx={isReply ? classes.replyAvatar : classes.avatar}
			/>
			<GridColumn sx={classes.message}>
				<div style={classes.header}>
					<span style={classes.username}>{userName}</span>
					<span style={classes.timestamp}>{timestamp}</span>
				</div>
				<ContentContainer>
					{messageType === ChatMessageType.MESSAGE ? (
						<>
							{importance === "HIGH" && (
								<Text
									res="Important"
									sx={classes.importantText}
								/>
							)}
							{subject &&
								<div style={classes.subject}>{subject}</div>
							}
							<FroalaEditorView model={richText} />
							{attachments.map((attachment) => (
								<AttachmentSection
									attachment={attachment}
									channelWebUrl={selectedChannelWebUrl}
									key={attachment.id}
								/>
							))}
							{reactionJSX.length > 0 && (
								<Box sx={classes.reactionsContainer}>
									{reactionJSX}
								</Box>
							)}
							{!selectedMessage && !isReply && (
								<Button
									design="linkSubtle"
									onClick={() => selectMessageReplies(message)}
									res={getLatestReplyFromMessage(message) ? "ViewAllReplies" : "Reply"}
									sx={classes.messageRepliesLink}
								/>
							)}
						</>
					)
						:
						<UnsupportedBlock link={selectedChannelWebUrl} linkTextProps={{ res: "MSTeamsUnsupportedMessageLink" }} />
					}
				</ContentContainer>
			</GridColumn>
		</GridRow>
	);
}));

export default Message;